
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// composable
import useNotifications from '@/common/composable/useNotifications'

// constants
import { SHIPMENT_TYPES } from '@/core/constants/create_order.constants'

// realisations
import { cities } from '@/core/realisations/cities.realisation'
import { billing } from '@/core/realisations/billing.realisation'

// validations
import useVuelidate from '@vuelidate/core'
import { UpdateTariff } from '@/modules/BillingModule/validations/UpdateTariff.validation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { ZoneT } from '@/core/types/cities.types'

// masks
import { useRoute, useRouter } from 'vue-router'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'the-input': TheInput,
        'the-select': TheSelect,
    },
    setup() {
        const route = useRoute()
        const router = useRouter()
        const tariff_id = Number(route.params.tariff_id as string)

        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            //
            zoneId: null as number | null,
            typeId: null as number | null,
            weight: null as number | null,
            minimumPrice: null as number | null,
            cost: null as number | null,
        })

        const state_form$ = useVuelidate(UpdateTariff, state)

        const progress = reactive({
            is_loading: false,
            is_failed: false,
        })

        const data_state = reactive({
            zones: new DataState<ZoneT>({}),
        })

        const loadZones = async (page: number, search?: string) => {
            data_state.zones.pagination.current_page = page

            try {
                data_state.zones.is_loading = true

                const data = await cities.getZonesList(page, search)
                data_state.zones.list = data.data

                data_state.zones.pagination.total_pages = data.meta.last_page

                data_state.zones.is_failed = false
                data_state.zones.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.zones.failure.catchError(error)

                if (!data_state.zones.failure.error_state.is_cancel) {
                    data_state.zones.is_failed = true
                }
            } finally {
                if (!data_state.zones.failure.error_state.is_cancel) {
                    data_state.zones.is_loading = false
                }
            }
        }

        const loadMoreZones = async (page: number) => {
            if (page > data_state.zones.pagination.total_pages) return
            data_state.zones.pagination.current_page = page

            try {
                data_state.zones.is_loading = true

                const response = await cities.getZonesList(page)
                data_state.zones.list = data_state.zones.list.concat(response.data)

                data_state.zones.pagination.total_pages = response.meta.last_page

                data_state.zones.is_failed = false
                data_state.zones.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.zones.failure.catchError(error)

                if (!data_state.zones.failure.error_state.is_cancel) {
                    data_state.zones.is_failed = true
                }
            } finally {
                if (!data_state.zones.failure.error_state.is_cancel) {
                    data_state.zones.is_loading = false
                }
            }
        }

        const searchZone = (search_query: string) => {
            loadZones(1, search_query)
        }

        const unshiftFoundZone = async (zone: ZoneT) => {
            await loadZones(data_state.zones.pagination.current_page)
            data_state.zones.list.unshift(zone)
        }

        const loadTariff = async () => {
            try {
                state.is_loading = true

                const response = await billing.getTariff(tariff_id)

                state.zoneId = response.data.zone.id
                state.typeId = response.data.type.id
                state.weight = response.data.weight
                state.minimumPrice = response.data.minimumPrice
                state.cost = response.data.cost

                state.is_failed = false
            } catch (error) {
                console.error(error)
                state.is_failed = true

                notifications.notifyError({
                    error,
                    title: 'При получении данных по тарифу произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadTariff()
            loadZones(1)
        })

        const onSubmit = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                progress.is_loading = true

                if (!state.zoneId || !state.typeId || !state.weight) return

                const payload = {
                    zoneId: state.zoneId,
                    typeId: state.typeId,
                    weight: state.weight,
                    minimumPrice: state.minimumPrice === 0 ? 0 : state.minimumPrice || undefined,
                    cost: state.cost === 0 ? 0 : state.cost,
                }

                await billing.updateTariff(tariff_id, payload)

                notifications.notify({ title: 'Тариф успешно отредактирован!', type: 'success' })
                progress.is_failed = false
                await router.push('/tariffs')
            } catch (error) {
                console.error(error)
                progress.is_failed = true

                notifications.notify({
                    title: 'При редактировании тарифа произошла ошибка!',
                    type: 'danger',
                })
            } finally {
                progress.is_loading = false
            }
        }

        return {
            SHIPMENT_TYPES,
            state,
            state_form$,
            data_state,
            loadZones,
            loadMoreZones,
            searchZone,
            unshiftFoundZone,
            progress,
            onSubmit,
        }
    },
})
